/**
 * Role-specific authentication middleware
 * This middleware ensures the user is authenticated with the specific role
 */
import { watch } from 'vue';
import { useRuntimeConfig } from '#app';
import { useAzureAdAuth, USER_ROLES } from '~/composables/useAzureAdAuth';
import { refreshTokenDirect } from '~/auth/refreshToken';

// Debug flags
const DEBUG_MIDDLEWARE = true;

/**
 * Creates a middleware that checks if the user is authenticated with a specific role
 * @param requiredRole The role required for accessing the route
 * @returns A middleware function
 */
export function createRoleMiddleware(requiredRole: string) {
  return defineNuxtRouteMiddleware(async (to, _from) => {
    // Respect runtime flag to disable auth for testing
    const config = useRuntimeConfig();
    const authEnabled = String(config.public.authEnabled).toLowerCase() === 'true';
    if (!authEnabled) {
      if (DEBUG_MIDDLEWARE) console.log(`[MIDDLEWARE] ${requiredRole} auth - Auth disabled via config, skipping`);
      return;
    }

    // Log entry point
    if (DEBUG_MIDDLEWARE) {
      console.log(`[MIDDLEWARE] ${requiredRole} auth - START`, {
        path: to.fullPath,
        timestamp: new Date().toISOString()
      });
    }

    // Skip middleware in server-side rendering
    if (import.meta.server) {
      if (DEBUG_MIDDLEWARE) console.log(`[MIDDLEWARE] ${requiredRole} auth - Server side, skipping`);
      return;
    }

    // Get the authentication composable
    const azureAdAuth = useAzureAdAuth();

    // Initialize auth if not already initialized
    azureAdAuth.initialize();
    if (DEBUG_MIDDLEWARE) console.log(`[MIDDLEWARE] ${requiredRole} auth - Initialized`);

    // Check authentication status
    const { isAuthenticated, isLoading, error, user } = azureAdAuth;
    if (DEBUG_MIDDLEWARE) {
      console.log(`[MIDDLEWARE] ${requiredRole} auth - Auth status:`, {
        isAuthenticated: isAuthenticated.value,
        isLoading: isLoading.value,
        hasError: !!error.value,
        hasUser: !!user.value,
        userExpired: user.value?.expired
      });
    }

    // If still loading, wait a moment
    if (isLoading.value) {
      try {
        await new Promise<void>((resolve, reject) => {
          const timeout = setTimeout(() => {
            reject(new Error('Authentication check timed out'));
          }, 5000);

          const unwatch = watch(isLoading, (loading: boolean) => {
            if (!loading) {
              clearTimeout(timeout);
              unwatch();
              resolve();
            }
          });
        });
      } catch (err) {
        console.error('Authentication loading timeout:', err);
      }
    }

    // If there was an authentication error, redirect to error page
    if (error.value) {
      console.error('Authentication error:', error.value);
      return navigateTo('/auth/error?error=' + encodeURIComponent(error.value.message));
    }

    // If not authenticated, try to refresh the token first before redirecting to login
    if (!isAuthenticated.value) {
      if (DEBUG_MIDDLEWARE) console.log(`[MIDDLEWARE] ${requiredRole} auth - Not authenticated, checking for refresh token`);

      // Check if user exists but token is expired (potential refresh case)
      if (user.value && user.value.refresh_token) {
        console.log(`[MIDDLEWARE] ${requiredRole} auth - Token expired but refresh token available. Attempting refresh...`);
        try {
          // Try to refresh the token
          console.log(`[MIDDLEWARE] ${requiredRole} auth - Calling refreshTokenDirect()`);
          const newToken = await refreshTokenDirect();
          console.log(`[MIDDLEWARE] ${requiredRole} auth - refreshTokenDirect result:`, !!newToken);

          if (newToken) {
            console.log(`[MIDDLEWARE] ${requiredRole} auth - Token refreshed successfully, continuing navigation`);
            // Force refresh the authentication state to reflect the new token
            console.log(`[MIDDLEWARE] ${requiredRole} auth - Refreshing auth state`);
            await azureAdAuth.refreshAuthState();
            console.log(`[MIDDLEWARE] ${requiredRole} auth - Auth state refreshed`);

            // Get updated user after refresh
            const updatedUser = await azureAdAuth.getManager()?.getUser();
            console.log(`[MIDDLEWARE] ${requiredRole} auth - Updated user:`, !!updatedUser, updatedUser?.expired);

            if (updatedUser) {
              // Continue with role check using the updated user
              let userRole = '';

              // Try to get the role from user claims or state
              if (updatedUser.state &&
                typeof updatedUser.state === 'object' &&
                'userRole' in updatedUser.state) {
                userRole = (updatedUser.state as { userRole: string }).userRole;
              } else if (updatedUser.profile) {
                // Determine role based on presence of phone number claim
                const hasPhoneNumber = 'phoneNumber' in updatedUser.profile;
                userRole = hasPhoneNumber ? USER_ROLES.PICKUP_PERSON : USER_ROLES.ADMIN;
              }

              // If user doesn't have the required role, redirect to unauthorized page
              if (userRole !== requiredRole) {
                console.warn(`User has role ${userRole} but ${requiredRole} is required`);
                return navigateTo('/auth/unauthorized?requiredRole=' + encodeURIComponent(requiredRole));
              }

              // If we get here, the user has the correct role after token refresh
              return;
            }
          } else {
            console.log('Token refresh failed, redirecting to login');
          }
        } catch (refreshError) {
          console.error('Error refreshing token:', refreshError);
        }
      }

      // Store the original destination for after login
      sessionStorage.setItem('authRedirect', to.fullPath);
      console.log(`[MIDDLEWARE] ${requiredRole} auth - Stored redirect path:`, to.fullPath);

      // Redirect to login page with role parameter
      console.log(`[MIDDLEWARE] ${requiredRole} auth - Redirecting to login with role:`, requiredRole);
      return navigateTo(`/auth/login?role=${requiredRole}`);
    }

    // Check if the user has the required role
    let userRole = '';

    // Try to get the role from user claims or state
    if (user.value && user.value.state &&
      typeof user.value.state === 'object' &&
      'userRole' in user.value.state) {
      userRole = (user.value.state as { userRole: string }).userRole;
      if (DEBUG_MIDDLEWARE) console.log(`[MIDDLEWARE] ${requiredRole} auth - Found user role in state:`, userRole);
    } else if (user.value && user.value.profile) {
      // Determine role based on presence of phone number claim
      const hasPhoneNumber = 'phoneNumber' in user.value.profile;
      userRole = hasPhoneNumber ? USER_ROLES.PICKUP_PERSON : USER_ROLES.ADMIN;
      if (DEBUG_MIDDLEWARE) console.log(`[MIDDLEWARE] ${requiredRole} auth - Determined user role from profile:`, userRole, hasPhoneNumber);
    }

    // If user doesn't have the required role, redirect to unauthorized page
    if (userRole !== requiredRole) {
      console.warn(`[MIDDLEWARE] ${requiredRole} auth - User has role ${userRole} but ${requiredRole} is required`);
      return navigateTo('/auth/unauthorized?requiredRole=' + encodeURIComponent(requiredRole));
    }

    if (DEBUG_MIDDLEWARE) console.log(`[MIDDLEWARE] ${requiredRole} auth - COMPLETE - user authorized with role:`, userRole);
  });
}

// Export pre-configured middleware for common roles
export const adminAuth = createRoleMiddleware(USER_ROLES.ADMIN);
export const pickupPersonAuth = createRoleMiddleware(USER_ROLES.PICKUP_PERSON);
